import React, { useEffect, useState } from '../vendor/react';
import { ArrowRight, CircleCheck, Copy, Download, Mic, RotateCcw, Search, Share2, Sparkles, X } from '../vendor/lucide-react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Link, useLocation, useNavigate } from '../vendor/react-router-dom';
import { usePhase13 } from '../features/phase13/usePhase13';
import { isAiActionInstruction } from '../features/phase13/phase13-store';
import { askProductionAi } from '../features/production/cloud-api';
import { runtimeConfig } from '../config/runtime';
import { formatMoney } from '../lib/money';
import type { AiActionProposal, AiAnswer } from '../types/domain';

const suggestions = [
  '¿Cómo van las ventas?',
  '¿Qué productos debo reponer?',
  '¿Cuál es mi utilidad estimada?',
  '¿Qué pedidos están pendientes?',
  'Prepara una lista de reposición',
  'Abre ventas',
];

const toolLabels: Record<string, string> = {
  'report.query': 'reporte del negocio',
  'sales.summary': 'ventas',
  'profit.estimate': 'utilidad estimada',
  'inventory.low_stock': 'inventario',
  'inventory.product_availability': 'existencias',
  'catalog.prices': 'catálogo',
  'memberships.summary': 'membresías',
  'branches.compare': 'sucursales',
  'products.rank': 'productos',
  'expenses.summary': 'gastos',
  'purchases.summary': 'compras',
  'orders.pending': 'pedidos',
  'customers.summary': 'clientes',
  'cash.sessions': 'caja',
  'payments.breakdown': 'formas de pago',
  'business.overview': 'resumen operativo',
  'operations.attention': 'prioridades operativas',
  'marketing.suggestions': 'oportunidades de promoción',
  'closures.latest': 'cierres',
  'closures.issues': 'diferencias de cierre',
  'periods.locked': 'periodos',
  'collections.summary': 'cobranza',
  'aging.calculate': 'antigüedad de saldos',
  'openai.responses': 'asistente en la nube',
  'business_report': 'reporte seguro',
  'cloud.fallback': 'respaldo local',
};

const historyKey = (businessId?: string) => `nexo.ai.conversation.${businessId || 'default'}`;
const readHistory = (businessId?: string): AiAnswer[] => {
  try {
    const value = JSON.parse(localStorage.getItem(historyKey(businessId)) ?? '[]') as AiAnswer[];
    return Array.isArray(value) ? value.slice(-20) : [];
  } catch { return []; }
};

export function NexoAiPage() {
  const phase = usePhase13();
  const navigate = useNavigate();
  const location = useLocation();
  const [answers, setAnswers] = useState<AiAnswer[]>(() => readHistory(phase.business?.id));
  const [proposal, setProposal] = useState<AiActionProposal | undefined>(undefined);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [listening, setListening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState('');

  useEffect(() => setAnswers(readHistory(phase.business?.id)), [phase.business?.id]);
  useEffect(() => {
    const prompt = new URLSearchParams(location.search).get('q');
    if (prompt) {
      const field = document.getElementById('nexo-ai-question') as HTMLInputElement | null;
      if (field) field.value = prompt;
    }
  }, [location.search]);

  const readQuestion = () => (document.getElementById('nexo-ai-question') as HTMLInputElement | null)?.value ?? '';
  const writeQuestion = (value: string, focus = false) => {
    const field = document.getElementById('nexo-ai-question') as HTMLInputElement | null;
    if (!field) return;
    field.value = value;
    if (focus) field.focus({ preventScroll: true });
  };

  const saveAnswers = (next: AiAnswer[]) => {
    const limited = next.slice(-20);
    setAnswers(limited);
    try { localStorage.setItem(historyKey(phase.business?.id), JSON.stringify(limited)); } catch { /* La conversación sigue disponible durante esta sesión. */ }
  };

  const ask = async (value?: string) => {
    const prompt = (value ?? readQuestion()).trim();
    if (!prompt || loading) return;
    setLoading(true);
    setError('');
    setNotice('');
    try {
      if (isAiActionInstruction(prompt)) {
        setProposal(phase.actions13.proposeAction(prompt));
      } else {
        let answer: AiAnswer;
        if (runtimeConfig.openAiEnabled && phase.business?.id) {
          try {
            const result = await askProductionAi({
              question: prompt,
              businessId: phase.business.id,
              branchId: phase.branch?.id,
              dateFrom: phase.report?.period.from,
              dateTo: phase.report?.period.to,
              history: answers.slice(-6).map((item) => ({ question: item.question, answer: item.answer })),
            });
            answer = { id: `cloud_${result.requestId}`, question: prompt, answer: result.answer, generatedAt: new Date().toISOString(), toolsUsed: ['openai.responses', 'business_report'], insights: phase.aiInsights9 };
          } catch {
            answer = phase.actions10.askNexo(prompt);
            answer.toolsUsed = [...answer.toolsUsed, 'cloud.fallback'];
            setNotice('La conexión con la IA en la nube no respondió. Contesté con los datos locales del negocio.');
          }
        } else {
          answer = phase.actions10.askNexo(prompt);
        }
        saveAnswers([...answers, answer]);
      }
      writeQuestion('');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se pudo analizar la solicitud.');
    } finally {
      setLoading(false);
    }
  };

  const listen = () => {
    const Ctor = (window as unknown as { SpeechRecognition?: new () => any; webkitSpeechRecognition?: new () => any }).SpeechRecognition || (window as unknown as { webkitSpeechRecognition?: new () => any }).webkitSpeechRecognition;
    if (!Ctor) { setError('El dictado no está disponible en este navegador. Puedes escribir la solicitud.'); return; }
    const recognition = new Ctor();
    recognition.lang = 'es-MX';
    recognition.interimResults = false;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => { setListening(false); setError('No se pudo usar el micrófono. Revisa el permiso del navegador.'); };
    recognition.onresult = (event: any) => { writeQuestion(event.results?.[0]?.[0]?.transcript ?? '', true); setListening(false); };
    recognition.start();
  };

  const execute = () => {
    if (!proposal) return;
    try {
      const executed = phase.actions13.executeAction(proposal.id);
      setProposal(undefined);
      const route = String(executed?.payload?.route ?? (executed?.kind === 'prepare-pos-cart' ? '/app/pos' : '/app/inventario'));
      navigate(route);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se pudo ejecutar la acción.');
    }
  };

  const clearConversation = () => {
    saveAnswers([]);
    setProposal(undefined);
    setNotice('Conversación nueva. Los datos del negocio no fueron modificados.');
  };

  const copyAnswer = async (answer: AiAnswer) => {
    try {
      await navigator.clipboard.writeText(answer.answer);
      setCopiedId(answer.id);
      window.setTimeout(() => setCopiedId(''), 1800);
    } catch { setError('No se pudo copiar la respuesta.'); }
  };

  const shareAnswer = async (answer: AiAnswer) => {
    const text = `${answer.question}\n\n${answer.answer}\n\n— NEXO IA`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Respuesta de NEXO IA', text });
        setNotice('Respuesta compartida.');
      } else {
        await navigator.clipboard.writeText(text);
        setNotice('Respuesta copiada para compartirla.');
      }
    } catch (reason: any) {
      if (reason?.name !== 'AbortError') setError('No se pudo compartir la respuesta.');
    }
  };

  const exportConversation = () => {
    const content = answers.map((answer) => `TÚ\n${answer.question}\n\nNEXO IA\n${answer.answer}`).join('\n\n------------------------------\n\n');
    const blob = new Blob([`NEXO COMERCIO — Conversación con NEXO IA\n${new Date().toLocaleString('es-MX')}\n\n${content}`], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `nexo-ia-${new Date().toISOString().slice(0, 10)}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    setNotice('Conversación exportada.');
  };

  const report = phase.report;
  const stats = [
    { label: 'Ventas del periodo', value: report ? formatMoney(report.salesCents) : 'Sin datos' },
    { label: 'Por cobrar', value: formatMoney(phase.debtSummary.receivableCents) },
    { label: 'Inventario bajo', value: String(report?.lowStockCount ?? 0) },
    { label: 'Pendientes', value: String(report?.pendingOrders ?? 0) },
  ];

  return <div className="page-stack ai-page">
    <section className="ai-hero ai-hero--assistant">
      <div><Badge tone="success"><Sparkles size={13} /> Asistente del negocio</Badge><h2>Pregunta, entiende y actúa con tus datos</h2><p>NEXO analiza la operación disponible, explica lo importante y prepara acciones para que tú las revises antes de ejecutarlas.</p></div>
      <div className="ai-status"><span>Modo actual</span><strong>{runtimeConfig.openAiEnabled ? 'Nube + local' : 'Local'}</strong><small>{runtimeConfig.openAiEnabled ? 'Con respaldo automático' : 'Funciona sin enviar datos fuera'}</small></div>
    </section>

    <section className="ai-context-strip">{stats.map((stat) => <article key={stat.label}><span>{stat.label}</span><strong>{stat.value}</strong></article>)}</section>

    <section className="ai-command panel">
      <div className="ai-command__heading"><div><strong>¿Qué necesitas saber o preparar?</strong><span>Pregunta normalmente o escribe una orden directa.</span></div>{answers.length > 0 && <div className="ai-command__actions"><Button variant="ghost" size="sm" icon={<Download size={15} />} onClick={exportConversation}>Exportar</Button><Button variant="ghost" size="sm" icon={<RotateCcw size={15} />} onClick={clearConversation}>Nueva conversación</Button></div>}</div>
      <form className="ai-input" onSubmit={(event) => { event.preventDefault(); void ask(); }}><Search size={19} /><input id="nexo-ai-question" name="question" placeholder="Ejemplo: ¿qué productos debo reponer?" aria-label="Pregunta para NEXO IA" autoComplete="off" /><button type="button" className={`ai-mic ${listening ? 'is-listening' : ''}`} onClick={listen} aria-label="Dictar solicitud"><Mic size={18} /></button><Button type="submit" disabled={loading}>{loading ? 'Analizando…' : 'Preguntar'}</Button></form>
      <div className="ai-suggestions">{suggestions.map((item) => <button key={item} onClick={() => void ask(item)}>{item}</button>)}</div>
      {notice && <p className="ai-notice"><CircleCheck size={15} />{notice}</p>}
      {error && <p className="form-error">{error}</p>}
    </section>

    {proposal && <section className="panel ai-proposal"><header><div><Badge tone={proposal.risk === 'read' ? 'info' : 'warning'}>{proposal.risk === 'read' ? 'Acción de navegación' : 'Requiere tu aprobación'}</Badge><h2>{proposal.title}</h2><p>{proposal.explanation}</p></div><button className="icon-button" aria-label="Cerrar propuesta" onClick={() => { phase.actions13.rejectAction(proposal.id); setProposal(undefined); }}><X size={18} /></button></header><div className="ai-proposal-preview">{proposal.preview.map((item) => <div key={item}><CircleCheck size={16} /><span>{item}</span></div>)}</div><footer><Button variant="ghost" onClick={() => { phase.actions13.rejectAction(proposal.id); setProposal(undefined); }}>Cancelar</Button><Button onClick={execute}>{proposal.kind === 'navigate' ? 'Abrir módulo' : 'Aprobar y continuar'}</Button></footer></section>}

    <section className="ai-grid ai-grid--conversation">
      <div className="ai-conversation">
        {answers.length ? answers.map((answer, index) => <article className="panel ai-answer" key={answer.id}>
          <div className="ai-question-bubble"><span>Tú</span><p>{answer.question}</p></div>
          <div className="ai-answer__body"><span className="ai-avatar"><Sparkles size={16} /></span><div><span>NEXO IA</span><p>{answer.answer}</p></div></div>
          <footer><small>Consultó {Array.from(new Set(answer.toolsUsed.map((tool) => toolLabels[tool] ?? tool))).join(' · ')}</small><div><time>{new Date(answer.generatedAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</time><button onClick={() => void copyAnswer(answer)} aria-label="Copiar respuesta"><Copy size={14} />{copiedId === answer.id ? 'Copiado' : 'Copiar'}</button><button onClick={() => void shareAnswer(answer)} aria-label="Compartir respuesta"><Share2 size={14} />Compartir</button></div></footer>
          {index === answers.length - 1 && <div className="ai-followups"><span>También puedes preguntar:</span><button onClick={() => void ask('¿Qué necesita atención hoy?')}>¿Qué necesita atención?</button><button onClick={() => void ask('¿Cómo puedo mejorar la utilidad?')}>¿Cómo mejorar la utilidad?</button></div>}
        </article>) : <article className="panel ai-empty"><span className="ai-empty__icon"><Sparkles size={27} /></span><h3>Empieza con una pregunta</h3><p>Prueba con ventas, utilidad, inventario, precios, pedidos, clientes, caja o compras. Para hacer algo, escribe una orden directa.</p><div><button onClick={() => void ask('¿Qué necesita atención hoy?')}>Resumir lo importante</button><button onClick={() => void ask('¿Qué productos debo reponer?')}>Revisar inventario</button></div></article>}
      </div>
      <aside className="panel ai-insights"><header className="panel__header"><div><span>Detección automática</span><h3>Lo que requiere atención</h3></div></header>{phase.aiInsights9.map((insight) => <article className={`ai-insight ai-insight--${insight.severity}`} key={insight.id}><div><strong>{insight.title}</strong>{insight.value && <em>{insight.value}</em>}</div><p>{insight.detail}</p>{insight.actionRoute && <Link to={insight.actionRoute}>{insight.actionLabel}<ArrowRight size={14} /></Link>}</article>)}</aside>
    </section>

    {!!phase.phase13?.aiActions.length && <section className="panel ai-audit"><div className="panel-heading"><div><span>Acciones recientes</span><h3>Historial del asistente</h3></div></div>{phase.phase13.aiActions.slice(0, 6).map((action) => <div className="ai-action-row" key={action.id}><div><strong>{action.title}</strong><small>{action.instruction}</small></div><Badge tone={action.status === 'executed' ? 'success' : action.status === 'rejected' ? 'danger' : 'warning'}>{action.status === 'executed' ? 'Ejecutada' : action.status === 'rejected' ? 'Cancelada' : 'Pendiente'}</Badge></div>)}</section>}
  </div>;
}
